import React from 'react';
import { Navigate } from 'react-router-dom';
import { useAuth } from '@bapecom/auth';

interface ProtectedRouteProps {
  children: React.ReactNode;
  appId: string;
  requireAdmin?: boolean;
}

export const ProtectedRoute: React.FC<ProtectedRouteProps> = ({ 
  children, 
  appId,
  requireAdmin = false,
}) => {
  const { user, isAuthenticated } = useAuth();

  if (!isAuthenticated || !user) {
    return <Navigate to="/" replace />;
  }

  // Admin has full access
  if (user.role === 'admin') {
    return <>{children}</>;
  }

  // Check admin-only routes
  if (requireAdmin) {
    return (
      <div className="min-h-[60vh] flex items-center justify-center">
        <div className="text-center">
          <div className="text-6xl mb-4">🔒</div>
          <h2 className="text-2xl font-bold text-gray-900 mb-2">Access Denied</h2>
          <p className="text-gray-600 mb-4">You need Admin privileges to access this page.</p>
          <a href="/" className="text-blue-600 hover:underline">← Back to Home</a>
        </div>
      </div>
    );
  }

  // Check user apps
  const userApps: string[] = (user as any).apps || [];
  
  if (!userApps.includes(appId)) {
    return (
      <div className="min-h-[60vh] flex items-center justify-center">
        <div className="text-center">
          <div className="text-6xl mb-4">🚫</div>
          <h2 className="text-2xl font-bold text-gray-900 mb-2">Access Denied</h2>
          <p className="text-gray-600 mb-4">You don't have permission to use this app.</p>
          <p className="text-sm text-gray-500 mb-4">Please contact Admin to get access.</p>
          <a href="/" className="text-blue-600 hover:underline">← Back to Home</a>
        </div>
      </div>
    );
  }

  return <>{children}</>;
};
